import React, { useState, useEffect } from 'react';
import { addDays, format } from 'date-fns';
import { ICON_STROKE } from '../../constants/icon-stroke';
import { getFontFamilyCSS } from '../../services/brand-settings';
import { Button, Label } from '../ui';
import { useIsMobile } from '../ui/use-mobile';
import { cn } from '../ui/utils';
import {
  bookingCalendarDayStyle,
  bookingCalendarFooterActionStyle,
  bookingCalendarNavMonthStyle,
  bookingCalendarWeekdayStyle,
  bookingDatePickerSurfaceStyle,
  bookingSettingsCardTitleStyle,
  bookingModalTitleStyle,
} from '../../styles/booking-typography';
import { bookingBrandFontFamilyVar, teboxDesignSystem, useBookingTebox } from '../../styles/tebox-booking';
import { BOOKING_PLAYER_COUNT_OPTIONS, MAX_PLAYERS } from '@boubou/booking-engine';

/** Outline chips: brand CSS vars for default + hover (avoids shadcn accent vs inline color clash) */
const BOOKING_PILL_CLASS =
  'booking-branded-pill hover:!translate-x-0 hover:!translate-y-0 hover:!shadow-none';

/** Course dropdown: round “satellite” ring + inner dot (multi-select), replaces square checkbox. */
const CourseSatelliteToggle: React.FC<{ isSelected: boolean; accentColor: string }> = ({
  isSelected,
  accentColor,
}) => {
  const tebox = useBookingTebox();
  return (
    <div
      role="presentation"
      className="flex shrink-0 items-center justify-center rounded-full border border-solid transition-[border-color,background-color] duration-150"
      style={{
        width: 18,
        height: 18,
        borderColor: isSelected ? accentColor : tebox.colors.border,
        backgroundColor: isSelected ? `${accentColor}1A` : '#FFFFFF',
      }}
    >
      {isSelected ? (
        <span
          className="block rounded-full"
          style={{
            width: 8,
            height: 8,
            backgroundColor: accentColor,
          }}
        />
      ) : null}
    </div>
  );
};

interface BrandColors {
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  fontFamily: string;
  buttonColor: string;
  buttonTextColor: string;
  primaryTextColor: string;
}

export interface BookingSearchFormProps {
  onSearch: (date: Date, players: number) => void;
  isLoading?: boolean;
  courses?: Array<{ id: string; name: string; color?: string }>;
  selectedCourseIds?: string[];
  onCourseChange?: (courseIds: string[]) => void;
  selectedTimeOfDay?: string;
  onTimeOfDayChange?: (timeOfDay: string) => void;
  selectedRoundType?: string;
  onRoundTypeChange?: (roundType: string) => void;
  brandColors?: BrandColors;
}

export const BookingSearchForm: React.FC<BookingSearchFormProps> = ({ 
  onSearch, 
  courses = [],
  selectedCourseIds = [],
  onCourseChange,
  selectedTimeOfDay = '',
  onTimeOfDayChange,
  selectedRoundType = '18-hole',
  onRoundTypeChange,
  brandColors: brandColorsProp,
}) => {
  const tebox = useBookingTebox();
  const brandColors = brandColorsProp ?? {
    primaryColor: '#000000',
    secondaryColor: tebox.colors.textSecondary,
    textColor: '#021B1B',
    fontFamily: 'Inter',
    buttonColor: '#000000',
    buttonTextColor: '#FFFFFF',
    primaryTextColor: '#FFFFFF'
  };
  const isMobile = useIsMobile();
  const [date, setDate] = useState<Date>(() => {
    // Ensure we start with today's date in local timezone
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    return today;
  });
  const [players, setPlayers] = useState<number>(2);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Update search results automatically when filters change
  useEffect(() => {
    if (date) {
      onSearch(date, players);
    }
  }, [date, players, onSearch]);

  // Handle clicks outside the date picker modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showDatePicker && !target.closest('.date-picker-container')) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Handle clicks outside the course dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showCourseDropdown && !target.closest('.course-dropdown-container')) {
        setShowCourseDropdown(false);
      }
    };

    if (showCourseDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCourseDropdown]);

  // Handle clicks outside mobile filter drawer
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showMobileFilters && !target.closest('.mobile-filters-container') && !target.closest('button')) {
        setShowMobileFilters(false);
      }
    };

    if (showMobileFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileFilters]);

  // Helper functions for calendar navigation
  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Disable dates in the past
    if (date < today) {
      return true;
    }
    
    // Disable dates more than 1 year in the future
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    oneYearFromNow.setHours(23, 59, 59, 999);
    
    return date > oneYearFromNow;
  };

  const handleDateSelect = (selectedDate: Date) => {
    if (!isDateDisabled(selectedDate)) {
      setDate(selectedDate);
      setShowDatePicker(false);
    }
  };

  // Generate today + next 5 days
  const dateOptions = Array.from({ length: 6 }, (_, i) => {
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0); // Reset time to start of day
    const date = addDays(baseDate, i);
    return {
      date,
      day: format(date, 'EEE'), // Mon, Tue, Wed, etc.
      dateNum: format(date, 'd'), // 1, 2, 3, etc.
      isToday: i === 0
    };
  });

  const handleCourseChange = (courseIds: string[]) => {
    if (onCourseChange) {
      onCourseChange(courseIds);
    }
  };

  const handleTimeOfDayChange = (timeOfDay: string) => {
    if (onTimeOfDayChange) {
      onTimeOfDayChange(timeOfDay);
    }
  };

  // Mobile: Show all filters in a collapsible drawer
  if (isMobile) {
    return (
      <div className="space-y-4">
        {/* Mobile: Filters Button - Always Visible */}
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="h-10 px-4 flex items-center gap-2"
            style={{
              borderColor: showMobileFilters ? brandColors.buttonColor : tebox.colors.border,
              backgroundColor: showMobileFilters ? brandColors.buttonColor : 'transparent',
              color: showMobileFilters ? brandColors.primaryTextColor : brandColors.textColor,
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: teboxDesignSystem.typography.caption.fontSize,
              fontWeight: '500'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 10H15M2.5 5H17.5M7.5 15H12.5" stroke={showMobileFilters ? brandColors.primaryTextColor : brandColors.textColor} strokeWidth={ICON_STROKE.standard} strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </div>

        {/* Mobile Filter Drawer - All Filters Stacked */}
        {showMobileFilters && (
          <div className="mobile-filters-container bg-white border border-tebox-border rounded-[6px] p-4 space-y-5">
            {/* Date Selection */}
            <div className="space-y-2">
              <Label htmlFor="date" className="block" style={bookingSettingsCardTitleStyle(brandColors.fontFamily)}>Date</Label>
              <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {dateOptions.map((option) => {
                  const dateSelected = date.toDateString() === option.date.toDateString();
                  return (
                  <Button
                    key={option.date.toISOString()}
                    type="button"
                    variant={dateSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDate(option.date)}
                    data-selected={dateSelected ? 'true' : 'false'}
                    className={cn(
                      'flex flex-col items-center justify-center gap-0 w-12 h-12 p-1 text-center flex-shrink-0 leading-none',
                      BOOKING_PILL_CLASS
                    )}
                    style={{
                      backgroundColor: dateSelected ? brandColors.buttonColor : undefined,
                      color: dateSelected ? brandColors.primaryTextColor : undefined,
                      borderColor: dateSelected ? brandColors.buttonColor : tebox.colors.border,
                      fontFamily: bookingBrandFontFamilyVar,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontSize: teboxDesignSystem.typography.caption.fontSize,
                        fontWeight: '500',
                        lineHeight: 1,
                        color: dateSelected ? brandColors.primaryTextColor : 'inherit',
                      }}
                    >
                      {option.isToday ? 'Today' : option.day}
                    </span>
                    <span
                      style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        fontWeight: '500',
                        lineHeight: 1,
                        color: dateSelected ? brandColors.primaryTextColor : 'inherit',
                      }}
                    >
                      {option.dateNum}
                    </span>
                  </Button>
                  );
                })}
                <div className="relative date-picker-container flex-shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDatePicker(true)}
                    data-selected="false"
                    className={cn('flex items-center justify-center w-12 h-12 p-1', BOOKING_PILL_CLASS)}
                    title="Pick a custom date"
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.5 8.33342H2.5M13.3333 1.66675V5.00008M6.66667 1.66675V5.00008M6.5 18.3334H13.5C14.9001 18.3334 15.6002 18.3334 16.135 18.0609C16.6054 17.8212 16.9878 17.4388 17.2275 16.9684C17.5 16.4336 17.5 15.7335 17.5 14.3334V7.33342C17.5 5.93328 17.5 5.23322 17.2275 4.69844C16.9878 4.22803 16.6054 3.84558 16.135 3.6059C15.6002 3.33341 14.9001 3.33341 13.5 3.33341H6.5C5.09987 3.33341 4.3998 3.33341 3.86502 3.6059C3.39462 3.84558 3.01217 4.22803 2.77248 4.69844C2.5 5.23322 2.5 5.93328 2.5 7.33341V14.3334C2.5 15.7335 2.5 16.4336 2.77248 16.9684C3.01217 17.4388 3.39462 17.8212 3.86502 18.0609C4.3998 18.3334 5.09987 18.3334 6.5 18.3334Z" stroke="currentColor" strokeWidth={ICON_STROKE.standard} strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Button>
                </div>
              </div>
            </div>

            {/* Players Selection */}
            <div className="space-y-2">
              <Label htmlFor="players" className="block" style={bookingSettingsCardTitleStyle(brandColors.fontFamily)}>Number of Players</Label>
              <div className="flex items-center gap-2">
                {BOOKING_PLAYER_COUNT_OPTIONS.map((num) => (
                  <Button
                    key={num}
                    type="button"
                    variant={players === num ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPlayers(num)}
                    data-selected={players === num ? 'true' : 'false'}
                    className={cn('w-12 h-12 text-tebox-body font-medium text-center', BOOKING_PILL_CLASS)}
                    style={{
                      backgroundColor: players === num ? brandColors.buttonColor : undefined,
                      color: players === num ? brandColors.primaryTextColor : undefined,
                      borderColor: players === num ? brandColors.buttonColor : tebox.colors.border,
                      fontFamily: bookingBrandFontFamilyVar
                    }}
                  >
                    {num}
                  </Button>
                ))}
              </div>
            </div>

            {/* Time of Day Selection */}
            <div className="space-y-2">
              <Label className="block" style={bookingSettingsCardTitleStyle(brandColors.fontFamily)}>Time of Day</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant={selectedTimeOfDay === 'morning' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleTimeOfDayChange('morning')}
                  data-selected={selectedTimeOfDay === 'morning' ? 'true' : 'false'}
                  className={cn('flex-1 h-12 text-tebox-body font-medium', BOOKING_PILL_CLASS)}
                  style={{
                    backgroundColor: selectedTimeOfDay === 'morning' ? brandColors.buttonColor : undefined,
                    color: selectedTimeOfDay === 'morning' ? brandColors.primaryTextColor : undefined,
                    borderColor: selectedTimeOfDay === 'morning' ? brandColors.buttonColor : tebox.colors.border,
                    fontFamily: bookingBrandFontFamilyVar
                  }}
                >
                  AM
                </Button>
                <Button
                  type="button"
                  variant={selectedTimeOfDay === 'afternoon' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleTimeOfDayChange('afternoon')}
                  data-selected={selectedTimeOfDay === 'afternoon' ? 'true' : 'false'}
                  className={cn('flex-1 h-12 text-tebox-body font-medium', BOOKING_PILL_CLASS)}
                  style={{
                    backgroundColor: selectedTimeOfDay === 'afternoon' ? brandColors.buttonColor : undefined,
                    color: selectedTimeOfDay === 'afternoon' ? brandColors.primaryTextColor : undefined,
                    borderColor: selectedTimeOfDay === 'afternoon' ? brandColors.buttonColor : tebox.colors.border,
                    fontFamily: bookingBrandFontFamilyVar
                  }}
                >
                  PM
                </Button>
              </div>
            </div>

            {/* Round Type Selection */}
            <div className="space-y-2">
              <Label className="block" style={bookingSettingsCardTitleStyle(brandColors.fontFamily)}>Holes</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant={selectedRoundType === '9-hole' ? "default" : "outline"}
                  size="sm"
                  onClick={() => onRoundTypeChange && onRoundTypeChange('9-hole')}
                  data-selected={selectedRoundType === '9-hole' ? 'true' : 'false'}
                  className={cn('flex-1 h-12 text-tebox-body font-medium', BOOKING_PILL_CLASS)}
                  style={{
                    backgroundColor: selectedRoundType === '9-hole' ? brandColors.buttonColor : undefined,
                    color: selectedRoundType === '9-hole' ? brandColors.primaryTextColor : undefined,
                    borderColor: selectedRoundType === '9-hole' ? brandColors.buttonColor : tebox.colors.border,
                    fontFamily: bookingBrandFontFamilyVar
                  }}
                >
                  9
                </Button>
                <Button
                  type="button"
                  variant={selectedRoundType === '18-hole' ? "default" : "outline"}
                  size="sm"
                  onClick={() => onRoundTypeChange && onRoundTypeChange('18-hole')}
                  data-selected={selectedRoundType === '18-hole' ? 'true' : 'false'}
                  className={cn('flex-1 h-12 text-tebox-body font-medium', BOOKING_PILL_CLASS)}
                  style={{
                    backgroundColor: selectedRoundType === '18-hole' ? brandColors.buttonColor : undefined,
                    color: selectedRoundType === '18-hole' ? brandColors.primaryTextColor : undefined,
                    borderColor: selectedRoundType === '18-hole' ? brandColors.buttonColor : tebox.colors.border,
                    fontFamily: bookingBrandFontFamilyVar
                  }}
                >
                  18
                </Button>
              </div>
            </div>

            {/* Course Selection */}
            <div className="space-y-2">
              <Label htmlFor="course" className="block" style={bookingSettingsCardTitleStyle(brandColors.fontFamily)}>Course</Label>
              <div className="flex items-center">
                <div className="relative w-full course-dropdown-container">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                    data-selected="false"
                    className={cn(
                      'flex items-center justify-between w-full h-12 px-3 text-left text-tebox-body',
                      BOOKING_PILL_CLASS,
                    )}
                    style={{
                      borderColor: tebox.colors.border,
                      color: brandColors.textColor,
                      fontFamily: bookingBrandFontFamilyVar,
                    }}
                  >
                    <span className="truncate text-tebox-body">
                      {selectedCourseIds.length === 0 
                        ? 'All Courses' 
                        : selectedCourseIds.length === 1 
                          ? courses.find(c => c.id === selectedCourseIds[0])?.name || 'Course'
                          : `${selectedCourseIds.length} courses selected`
                      }
                    </span>
                    <svg 
                      className={`w-4 h-4 transition-transform ${showCourseDropdown ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ICON_STROKE.standard} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                  
                  {/* Course Dropdown */}
                  {showCourseDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#ffffff',
                      border: `1px solid ${tebox.colors.border}`,
                      borderRadius: 'var(--brand-border-radius, 6px)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      marginTop: '2px'
                    }}>
                      {courses.map((course) => {
                        const isSelected = selectedCourseIds.includes(course.id);
                        return (
                          <div
                            key={course.id}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: teboxDesignSystem.typography.body.fontSize,
                              fontWeight: '500',
                              fontFamily: bookingBrandFontFamilyVar,
                              color: brandColors.textColor,
                              transition: 'background-color 0.15s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              backgroundColor: isSelected ? `${brandColors.buttonColor}0D` : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = `${brandColors.buttonColor}1A`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isSelected ? `${brandColors.buttonColor}0D` : 'transparent';
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isSelected) {
                                const newCourseIds = selectedCourseIds.filter(id => id !== course.id);
                                handleCourseChange(newCourseIds);
                              } else {
                                const newCourseIds = [...selectedCourseIds, course.id];
                                handleCourseChange(newCourseIds);
                              }
                            }}
                          >
                            <CourseSatelliteToggle
                              isSelected={isSelected}
                              accentColor={brandColors.buttonColor}
                            />

                            {/* Course Color Dot */}
                            <div 
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: course.color || tebox.colors.textSecondary }}
                            />
                            
                            {/* Course Name */}
                            <span>{course.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Date Picker Modal for Mobile */}
        {showDatePicker && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-[6px] p-6 w-full max-w-sm shadow-lg"
              style={bookingDatePickerSurfaceStyle(brandColors.fontFamily)}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="m-0" style={bookingModalTitleStyle(brandColors.fontFamily)}>Select Date</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDatePicker(false)}
                  className="w-8 h-8 p-0"
                  style={{ fontFamily: getFontFamilyCSS(brandColors.fontFamily) }}
                >
                  ×
                </Button>
              </div>

              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigateMonth('prev')}
                  className="w-8 h-8 p-0"
                  style={{ fontFamily: getFontFamilyCSS(brandColors.fontFamily) }}
                >
                  ‹
                </Button>
                <span style={bookingCalendarNavMonthStyle(brandColors.fontFamily)}>
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigateMonth('next')}
                  className="w-8 h-8 p-0"
                  style={{ fontFamily: getFontFamilyCSS(brandColors.fontFamily) }}
                >
                  ›
                </Button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {/* Day Headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center py-2" style={bookingCalendarWeekdayStyle(brandColors.fontFamily)}>
                    {day}
                  </div>
                ))}
                
                {/* Calendar Days */}
                {(() => {
                  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
                  const days = [];
                  
                  // Add empty cells for days before the first day of the month
                  for (let i = 0; i < startingDayOfWeek; i++) {
                    days.push(<div key={`empty-${i}`} className="py-2" />);
                  }
                  
                  // Add days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                    const isDisabled = isDateDisabled(currentDate);
                    const isSelected = date.toDateString() === currentDate.toDateString();
                    const isToday = new Date().toDateString() === currentDate.toDateString();
                    
                    days.push(
                      <button
                        key={day}
                        onClick={() => handleDateSelect(currentDate)}
                        disabled={isDisabled}
                        className={`
                          py-2 px-1 rounded-[6px] transition-colors
                          ${isDisabled 
                            ? 'text-tebox-subtle cursor-not-allowed' 
                            : isSelected 
                              ? 'bg-black text-white' 
                              : isToday 
                                ? 'bg-tebox-secondary text-black hover:bg-tebox-hover' 
                                : 'text-black hover:bg-tebox-hover'
                          }
                        `}
                        style={bookingCalendarDayStyle(brandColors.fontFamily)}
                      >
                        {day}
                      </button>
                    );
                  }
                  
                  return days;
                })()}
              </div>

              {/* Quick Actions */}
              <div className="flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    setDate(today);
                    setCurrentMonth(today);
                    setShowDatePicker(false);
                  }}
                  className="flex-1"
                  style={bookingCalendarFooterActionStyle(brandColors.fontFamily)}
                >
                  Today
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setDate(tomorrow);
                    setCurrentMonth(tomorrow);
                    setShowDatePicker(false);
                  }}
                  className="flex-1"
                  style={bookingCalendarFooterActionStyle(brandColors.fontFamily)}
                >
                  Tomorrow
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop: single horizontal row (scrolls when needed)
  return (
    <div className="flex flex-nowrap items-start gap-5 lg:gap-7 w-full min-w-0">
        {/* Date — outside horizontal scroll so calendar popover is not clipped */}
        <div className="flex flex-col gap-2 shrink-0">
          <Label htmlFor="date" className="block" style={bookingSettingsCardTitleStyle(brandColors.fontFamily)}>Date</Label>
          <div className="flex items-center gap-2 h-12 flex-nowrap">
            {dateOptions.map((option) => {
              const dateSelected = date.toDateString() === option.date.toDateString();
              return (
              <Button
                key={option.date.toISOString()}
                type="button"
                variant={dateSelected ? "default" : "outline"}
                size="sm"
                onClick={() => setDate(option.date)}
                data-selected={dateSelected ? 'true' : 'false'}
                className={cn(
                  'flex flex-col items-center justify-center gap-0 w-12 h-12 p-1 text-center flex-shrink-0 leading-none',
                  dateSelected ? 'text-white' : '',
                  BOOKING_PILL_CLASS
                )}
                style={{
                  backgroundColor: dateSelected ? brandColors.buttonColor : undefined,
                  color: dateSelected ? brandColors.primaryTextColor : undefined,
                  borderColor: dateSelected ? brandColors.buttonColor : tebox.colors.border,
                  fontFamily: bookingBrandFontFamilyVar
                }}
              >
                <span
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontSize: teboxDesignSystem.typography.caption.fontSize,
                    fontWeight: '500',
                    lineHeight: 1,
                    color: dateSelected ? brandColors.primaryTextColor : 'inherit',
                  }}
                >
                  {option.isToday ? 'Today' : option.day}
                </span>
                <span
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontSize: teboxDesignSystem.typography.body.fontSize,
                    fontWeight: '500',
                    lineHeight: 1,
                    color: dateSelected ? brandColors.primaryTextColor : 'inherit',
                  }}
                >
                  {option.dateNum}
                </span>
              </Button>
              );
            })}

            {/* Custom Date Picker Button */}
            <div className="relative date-picker-container flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowDatePicker(true)}
                data-selected="false"
                className={cn('flex items-center justify-center w-12 h-12 p-1', BOOKING_PILL_CLASS)}
                title="Pick a custom date"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M17.5 8.33342H2.5M13.3333 1.66675V5.00008M6.66667 1.66675V5.00008M6.5 18.3334H13.5C14.9001 18.3334 15.6002 18.3334 16.135 18.0609C16.6054 17.8212 16.9878 17.4388 17.2275 16.9684C17.5 16.4336 17.5 15.7335 17.5 14.3334V7.33342C17.5 5.93328 17.5 5.23322 17.2275 4.69844C16.9878 4.22803 16.6054 3.84558 16.135 3.6059C15.6002 3.33341 14.9001 3.33341 13.5 3.33341H6.5C5.09987 3.33341 4.3998 3.33341 3.86502 3.6059C3.39462 3.84558 3.01217 4.22803 2.77248 4.69844C2.5 5.23322 2.5 5.93328 2.5 7.33341V14.3334C2.5 15.7335 2.5 16.4336 2.77248 16.9684C3.01217 17.4388 3.39462 17.8212 3.86502 18.0609C4.3998 18.3334 5.09987 18.3334 6.5 18.3334Z" stroke="currentColor" strokeWidth={ICON_STROKE.standard} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Button>
              
              {/* Date Picker Modal */}
              {showDatePicker && (
                <div className="absolute top-full left-0 mt-2 z-50">
                  <div
                    className="bg-white rounded-[6px] p-6 w-80 max-w-sm shadow-lg border border-tebox-border"
                    style={{ ...bookingDatePickerSurfaceStyle(brandColors.fontFamily), minWidth: '320px' }}
                  >
                    {/* Modal Header */}
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="m-0" style={bookingModalTitleStyle(brandColors.fontFamily)}>Select Date</h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDatePicker(false)}
                        className="w-8 h-8 p-0"
                        style={{ fontFamily: getFontFamilyCSS(brandColors.fontFamily) }}
                      >
                        ×
                      </Button>
                    </div>

                    {/* Month Navigation */}
                    <div className="flex items-center justify-between mb-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigateMonth('prev')}
                        className="w-8 h-8 p-0"
                        style={{ fontFamily: getFontFamilyCSS(brandColors.fontFamily) }}
                      >
                        ‹
                      </Button>
                      <span style={bookingCalendarNavMonthStyle(brandColors.fontFamily)}>
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigateMonth('next')}
                        className="w-8 h-8 p-0"
                        style={{ fontFamily: getFontFamilyCSS(brandColors.fontFamily) }}
                      >
                        ›
                      </Button>
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1 mb-4">
                      {/* Day Headers */}
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center py-2" style={bookingCalendarWeekdayStyle(brandColors.fontFamily)}>
                          {day}
                        </div>
                      ))}
                      
                      {/* Calendar Days */}
                      {(() => {
                        const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
                        const days = [];
                        
                        // Add empty cells for days before the first day of the month
                        for (let i = 0; i < startingDayOfWeek; i++) {
                          days.push(<div key={`empty-${i}`} className="py-2" />);
                        }
                        
                        // Add days of the month
                        for (let day = 1; day <= daysInMonth; day++) {
                          const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                          const isDisabled = isDateDisabled(currentDate);
                          const isSelected = date.toDateString() === currentDate.toDateString();
                          const isToday = new Date().toDateString() === currentDate.toDateString();
                          
                          days.push(
                            <button
                              key={day}
                              onClick={() => handleDateSelect(currentDate)}
                              disabled={isDisabled}
                              className={`
                                py-2 px-1 rounded-[6px] transition-colors
                                ${isDisabled 
                                  ? 'text-tebox-subtle cursor-not-allowed' 
                                  : isSelected 
                                    ? 'bg-black text-white' 
                                    : isToday 
                                      ? 'bg-tebox-secondary text-black hover:bg-tebox-hover' 
                                      : 'text-black hover:bg-tebox-hover'
                                  }
                              `}
                              style={bookingCalendarDayStyle(brandColors.fontFamily)}
                            >
                              {day}
                            </button>
                          );
                        }
                        
                        return days;
                      })()}
                    </div>

                    {/* Quick Actions */}
                    <div className="flex space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const today = new Date();
                          setDate(today);
                          setCurrentMonth(today);
                          setShowDatePicker(false);
                        }}
                        className="flex-1"
                        style={bookingCalendarFooterActionStyle(brandColors.fontFamily)}
                      >
                        Today
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          setDate(tomorrow);
                          setCurrentMonth(tomorrow);
                          setShowDatePicker(false);
                        }}
                        className="flex-1"
                        style={bookingCalendarFooterActionStyle(brandColors.fontFamily)}
                      >
                        Tomorrow
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scroll only the middle filters; overflow-x clips absolutely positioned menus */}
        <div className="flex flex-nowrap items-start gap-5 lg:gap-7 min-w-0 flex-1 overflow-x-auto pb-0.5 scroll-smooth [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-tebox-border">
        {/* Players */}
        <div className="flex flex-col gap-2 shrink-0">
          <Label htmlFor="players" className="block" style={bookingSettingsCardTitleStyle(brandColors.fontFamily)}>Players</Label>
          <div className="flex items-center h-12 gap-2 flex-nowrap">
            {BOOKING_PLAYER_COUNT_OPTIONS.map((num) => (
              <Button
                key={num}
                type="button"
                variant={players === num ? "default" : "outline"}
                size="sm"
                onClick={() => setPlayers(num)}
                data-selected={players === num ? 'true' : 'false'}
                className={cn('w-12 h-12 text-tebox-body font-medium text-center', BOOKING_PILL_CLASS)}
                style={{
                  backgroundColor: players === num ? brandColors.buttonColor : undefined,
                  color: players === num ? brandColors.primaryTextColor : undefined,
                  borderColor: players === num ? brandColors.buttonColor : tebox.colors.border,
                  fontFamily: bookingBrandFontFamilyVar
                }}
              >
                {num}
              </Button>
            ))}
            
            {/* Group Reservation Button */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                // TODO: Implement group reservation request functionality
              }}
              data-selected="false"
              className={cn('flex items-center justify-center w-12 h-12 p-1', BOOKING_PILL_CLASS)}
              style={{
                borderColor: tebox.colors.border,
                fontFamily: bookingBrandFontFamilyVar,
              }}
              title={`Request group reservation (${MAX_PLAYERS + 1}+ players)`}
              aria-label={`Request group reservation (${MAX_PLAYERS + 1}+ players)`}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M15 13.1974C16.2133 13.8069 17.2534 14.785 18.0127 16.008C18.1631 16.2502 18.2382 16.3713 18.2642 16.539C18.3171 16.8798 18.084 17.2988 17.7666 17.4336C17.6104 17.5 17.4347 17.5 17.0833 17.5M13.3333 9.6102C14.5681 8.99657 15.4167 7.72238 15.4167 6.25C15.4167 4.77762 14.5681 3.50343 13.3333 2.8898M11.6667 6.25C11.6667 8.32107 9.98774 10 7.91667 10C5.8456 10 4.16667 8.32107 4.16667 6.25C4.16667 4.17893 5.8456 2.5 7.91667 2.5C9.98774 2.5 11.6667 4.17893 11.6667 6.25ZM2.13269 15.782C3.46128 13.7871 5.55781 12.5 7.91667 12.5C10.2755 12.5 12.3721 13.7871 13.7006 15.782C13.9917 16.219 14.1372 16.4375 14.1205 16.7166C14.1074 16.9339 13.965 17.2 13.7913 17.3313C13.5683 17.5 13.2615 17.5 12.648 17.5H3.1853C2.57181 17.5 2.26507 17.5 2.04203 17.3313C1.86837 17.2 1.72591 16.9339 1.71286 16.7166C1.69611 16.4375 1.84164 16.219 2.13269 15.782Z" stroke="currentColor" strokeWidth={ICON_STROKE.standard} strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Button>
          </div>

        </div>

        {/* Time */}
        <div className="flex flex-col gap-2 shrink-0">
          <Label className="block" style={bookingSettingsCardTitleStyle(brandColors.fontFamily)}>Time</Label>
          <div className="flex items-center h-12 gap-2 flex-nowrap">
            <Button
              type="button"
              variant={selectedTimeOfDay === 'morning' ? "default" : "outline"}
              size="sm"
              onClick={() => handleTimeOfDayChange('morning')}
              data-selected={selectedTimeOfDay === 'morning' ? 'true' : 'false'}
              className={cn('w-12 h-12 text-tebox-body font-medium', BOOKING_PILL_CLASS)}
              style={{
                backgroundColor: selectedTimeOfDay === 'morning' ? brandColors.buttonColor : undefined,
                color: selectedTimeOfDay === 'morning' ? brandColors.primaryTextColor : undefined,
                borderColor: selectedTimeOfDay === 'morning' ? brandColors.buttonColor : tebox.colors.border,
                fontFamily: bookingBrandFontFamilyVar
              }}
            >
              AM
            </Button>
            <Button
              type="button"
              variant={selectedTimeOfDay === 'afternoon' ? "default" : "outline"}
              size="sm"
              onClick={() => handleTimeOfDayChange('afternoon')}
              data-selected={selectedTimeOfDay === 'afternoon' ? 'true' : 'false'}
              className={cn('w-12 h-12 text-tebox-body font-medium', BOOKING_PILL_CLASS)}
              style={{
                backgroundColor: selectedTimeOfDay === 'afternoon' ? brandColors.buttonColor : undefined,
                color: selectedTimeOfDay === 'afternoon' ? brandColors.primaryTextColor : undefined,
                borderColor: selectedTimeOfDay === 'afternoon' ? brandColors.buttonColor : tebox.colors.border,
                fontFamily: bookingBrandFontFamilyVar
              }}
            >
              PM
            </Button>
          </div>

        </div>

        {/* Holes */}
        <div className="flex flex-col gap-2 shrink-0">
          <Label className="block" style={bookingSettingsCardTitleStyle(brandColors.fontFamily)}>Holes</Label>
          <div className="flex items-center h-12 gap-2 flex-nowrap">
            <Button
              type="button"
              variant={selectedRoundType === '9-hole' ? "default" : "outline"}
              size="sm"
              onClick={() => onRoundTypeChange && onRoundTypeChange('9-hole')}
              data-selected={selectedRoundType === '9-hole' ? 'true' : 'false'}
              className={cn('w-12 h-12 text-tebox-body font-medium', BOOKING_PILL_CLASS)}
              style={{
                backgroundColor: selectedRoundType === '9-hole' ? brandColors.buttonColor : undefined,
                color: selectedRoundType === '9-hole' ? brandColors.primaryTextColor : undefined,
                borderColor: selectedRoundType === '9-hole' ? brandColors.buttonColor : tebox.colors.border,
                fontFamily: bookingBrandFontFamilyVar
              }}
            >
              9
            </Button>
            <Button
              type="button"
              variant={selectedRoundType === '18-hole' ? "default" : "outline"}
              size="sm"
              onClick={() => onRoundTypeChange && onRoundTypeChange('18-hole')}
              data-selected={selectedRoundType === '18-hole' ? 'true' : 'false'}
              className={cn('w-12 h-12 text-tebox-body font-medium', BOOKING_PILL_CLASS)}
              style={{
                backgroundColor: selectedRoundType === '18-hole' ? brandColors.buttonColor : undefined,
                color: selectedRoundType === '18-hole' ? brandColors.primaryTextColor : undefined,
                borderColor: selectedRoundType === '18-hole' ? brandColors.buttonColor : tebox.colors.border,
                fontFamily: bookingBrandFontFamilyVar
              }}
            >
              18
            </Button>
          </div>

        </div>
        </div>

        {/* Course — outside scroll so the menu is not clipped; fixed width so it doesn’t stretch the row */}
        <div className="flex flex-col gap-2 w-52 sm:w-56 shrink-0 min-w-0">
          <Label htmlFor="course" className="block" style={bookingSettingsCardTitleStyle(brandColors.fontFamily)}>Course</Label>
          <div className="flex items-center h-12 min-w-0 w-full">
            <div className="relative w-full min-w-0 course-dropdown-container">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                data-selected="false"
                className={cn(
                  'flex items-center justify-between w-full min-w-0 max-w-full h-12 px-3 text-left text-tebox-body',
                  BOOKING_PILL_CLASS,
                )}
                style={{
                  borderColor: tebox.colors.border,
                  color: brandColors.textColor,
                  fontFamily: bookingBrandFontFamilyVar,
                }}
              >
                <span className="truncate text-tebox-body">
                  {selectedCourseIds.length === 0 
                    ? 'Course' 
                    : selectedCourseIds.length === 1 
                      ? courses.find(c => c.id === selectedCourseIds[0])?.name || 'Course'
                      : `${selectedCourseIds.length} courses selected`
                  }
                </span>
                <svg 
                  className={`w-4 h-4 transition-transform ${showCourseDropdown ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ICON_STROKE.standard} d="M19 9l-7 7-7-7" />
                </svg>
              </Button>
              
              {/* Course Dropdown */}
              {showCourseDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#ffffff',
                  border: `1px solid ${tebox.colors.border}`,
                  borderRadius: 'var(--brand-border-radius, 6px)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  marginTop: '2px'
                }}>
                  {courses.map((course) => {
                    const isSelected = selectedCourseIds.includes(course.id);
                    return (
                      <div
                        key={course.id}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          fontSize: teboxDesignSystem.typography.body.fontSize,
                          fontWeight: '500',
                          fontFamily: bookingBrandFontFamilyVar,
                          color: brandColors.textColor,
                          transition: 'background-color 0.15s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: isSelected ? `${brandColors.buttonColor}0D` : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${brandColors.buttonColor}1A`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isSelected ? `${brandColors.buttonColor}0D` : 'transparent';
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isSelected) {
                            const newCourseIds = selectedCourseIds.filter(id => id !== course.id);
                            handleCourseChange(newCourseIds);
                          } else {
                            const newCourseIds = [...selectedCourseIds, course.id];
                            handleCourseChange(newCourseIds);
                          }
                        }}
                      >
                        <CourseSatelliteToggle
                          isSelected={isSelected}
                          accentColor={brandColors.buttonColor}
                        />

                        {/* Course Color Dot */}
                        <div 
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: course.color || tebox.colors.textSecondary }}
                        />
                        
                        {/* Course Name */}
                        <span>{course.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};
